import { useState, useEffect, useRef } from "react";

import { Application } from "@chirpstack/chirpstack-api-grpc-web/api/application_pb";
import {
  DisableDevNonceCheckRequest,
  EnableDevNonceCheckRequest,
  GetDevNonceCheckStatusRequest,
  GetDevNonceCheckStatusResponse,
} from "@chirpstack/chirpstack-api-grpc-web/api/application_pb";
import { Form, Input, Button, Tabs, Row, Col, Space, Alert, Divider } from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";

import { onFinishFailed } from "../helpers";
import ApplicationStore from "../../stores/ApplicationStore";
import SessionStore from "../../stores/SessionStore";

interface IProps {
  initialValues: Application;
  onFinish: (obj: Application) => void;
  disabled?: boolean;
}

// Format remaining seconds as HH:MM:SS
function formatCountdown(totalSeconds: number): string {
  if (totalSeconds <= 0) {
    return "00:00:00";
  }
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const hh = String(hours).padStart(2, "0");
  const mm = String(minutes).padStart(2, "0");
  const ss = String(seconds).padStart(2, "0");

  if (days > 0) {
    return `${days}天 ${hh}:${mm}:${ss}`;
  }
  return `${hh}:${mm}:${ss}`;
}

function DevNonceSection(props: { appId: string; canEdit: boolean }) {
  const [status, setStatus] = useState<GetDevNonceCheckStatusResponse | null>(null);
  const [countdown, setCountdown] = useState<number>(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchStatus = () => {
    const req = new GetDevNonceCheckStatusRequest();
    req.setApplicationId(props.appId);
    ApplicationStore.getDevNonceCheckStatus(req, resp => {
      setStatus(resp);

      // Compute initial countdown from expires_at
      if (resp.getDisabled() && !resp.getPermanent() && resp.getExpiresAt()) {
        const expiresAt = resp.getExpiresAt()!;
        const expiresTs = expiresAt.getSeconds();
        const nowTs = Math.floor(Date.now() / 1000);
        setCountdown(Math.max(0, expiresTs - nowTs));
      } else {
        setCountdown(0);
      }
    });
  };

  useEffect(() => {
    fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.appId]);

  // Countdown ticker
  useEffect(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (status?.getDisabled() && !status?.getPermanent() && countdown > 0) {
      timerRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            // Expired — re-fetch status to sync with server
            fetchStatus();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, countdown > 0]);

  const handleDisable = (durationSecs: number) => {
    const req = new DisableDevNonceCheckRequest();
    req.setApplicationId(props.appId);
    req.setDurationSecs(durationSecs);
    ApplicationStore.disableDevNonceCheck(req, fetchStatus);
  };

  const handleEnable = () => {
    const req = new EnableDevNonceCheckRequest();
    req.setApplicationId(props.appId);
    ApplicationStore.enableDevNonceCheck(req, fetchStatus);
  };

  if (!status) {
    return null;
  }

  const isDisabled = status.getDisabled();
  const isPermanent = status.getPermanent();

  return (
    <div>
      <Divider orientation="left" plain>
        DevNonce 检查
      </Divider>

      {isDisabled ? (
        <Alert
          type="warning"
          showIcon
          message={
            isPermanent ? (
              <Space>
                <span>DevNonce 检查已永久禁用</span>
                {props.canEdit && (
                  <Button size="small" onClick={handleEnable}>
                    取消禁用
                  </Button>
                )}
              </Space>
            ) : (
              <Space>
                <span>
                  DevNonce 检查禁用中，剩余{" "}
                  <strong style={{ fontFamily: "monospace" }}>{formatCountdown(countdown)}</strong>
                </span>
                {props.canEdit && (
                  <Button size="small" onClick={handleEnable}>
                    取消禁用
                  </Button>
                )}
              </Space>
            )
          }
        />
      ) : (
        <Alert
          type="info"
          showIcon
          message={
            <Space>
              <span>DevNonce 检查正常</span>
              {props.canEdit && (
                <>
                  <span style={{ color: "#999", margin: "0 4px" }}>临时禁用：</span>
                  <Button size="small" onClick={() => handleDisable(3600)}>
                    +1小时
                  </Button>
                  <Button size="small" onClick={() => handleDisable(86400)}>
                    +1天
                  </Button>
                  <Button size="small" onClick={() => handleDisable(432000)}>
                    +5天
                  </Button>
                  <Button size="small" danger onClick={() => handleDisable(0)}>
                    永久禁用
                  </Button>
                </>
              )}
            </Space>
          }
        />
      )}
    </div>
  );
}

function ApplicationForm(props: IProps) {
  const onFinish = (values: Application.AsObject) => {
    const v = Object.assign(props.initialValues.toObject(), values);
    const app = new Application();

    app.setId(v.id);
    app.setTenantId(v.tenantId);
    app.setName(v.name);
    app.setDescription(v.description);

    // tags
    for (const elm of v.tagsMap) {
      app.getTagsMap().set(elm[0], elm[1]);
    }

    props.onFinish(app);
  };

  const appId = props.initialValues.getId();
  const canEdit =
    !props.disabled &&
    (SessionStore.isAdmin() ||
      SessionStore.isTenantAdmin(props.initialValues.getTenantId()) ||
      SessionStore.isTenantDeviceAdmin(props.initialValues.getTenantId()));

  return (
    <Form
      layout="vertical"
      initialValues={props.initialValues.toObject()}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
    >
      <Tabs>
        <Tabs.TabPane tab="General" key="1">
          <Form.Item label="Name" name="name" rules={[{ required: true, message: "Please enter a name!" }]}>
            <Input disabled={props.disabled} />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input.TextArea disabled={props.disabled} />
          </Form.Item>

          {/* Only show DevNonce section when we have an existing app (edit mode) */}
          {appId && <DevNonceSection appId={appId} canEdit={canEdit} />}
        </Tabs.TabPane>
        <Tabs.TabPane tab="Tags" key="2">
          <Form.List name="tagsMap">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Row gutter={24}>
                    <Col span={6}>
                      <Form.Item
                        {...restField}
                        name={[name, 0]}
                        fieldKey={[name, 0]}
                        rules={[{ required: true, message: "Please enter a key!" }]}
                      >
                        <Input placeholder="Key" />
                      </Form.Item>
                    </Col>
                    <Col span={16}>
                      <Form.Item
                        {...restField}
                        name={[name, 1]}
                        fieldKey={[name, 1]}
                        rules={[{ required: true, message: "Please enter a value!" }]}
                      >
                        <Input placeholder="Value" />
                      </Form.Item>
                    </Col>
                    <Col span={2}>
                      <MinusCircleOutlined onClick={() => remove(name)} />
                    </Col>
                  </Row>
                ))}
                <Form.Item>
                  <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                    Add tag
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </Tabs.TabPane>
      </Tabs>
      <Form.Item>
        <Button type="primary" htmlType="submit" disabled={props.disabled}>
          Submit
        </Button>
      </Form.Item>
    </Form>
  );
}

export default ApplicationForm;
